import {
  AiProvidersSection,
  OtherProvidersSection,
  useAiProviders,
} from '@/features/ai-providers';
import {
  LlmProfilesSection,
  useLlmProfiles,
  type LlmProfilesView,
} from '@/features/llm-profiles';

import { ADMIN_PAGE_SECTION } from '../adminLayout';
import { CONSOLE_ANSWER_COPY as ANSWER } from '../consoleCopy';
import { ConsoleAnswer, ConsoleHeader } from './ConsoleHeader';

/**
 * The combined AI Providers + LLM Profiles console page (spec
 * 084-ai-providers-admin FR-001), replacing the old `/admin/llm-profiles`
 * page. Both sections are independent feature packages; this page is the
 * one place that composes them, which is what keeps `ai-providers` and
 * `llm-profiles` from importing each other's page component (llm-profiles
 * already imports `useAiProviders` for its provider selector).
 *
 * Claude connections sit above LLM Profiles — a profile needs a connection
 * to reference, so setting one up comes first — while Other Providers,
 * always empty until another `provider_type` is creatable (FR-002a), sits
 * below the profiles a user actually cares about configuring.
 */
export interface AiProvidersAdminPageProps {
  /**
   * The LLM profiles grid's order and filter, from the route's search params
   * (spec 136 FR-011). Only the profiles section reads them; the AI providers and
   * other-providers sections have no view state in the address.
   */
  view?: LlmProfilesView;
  onViewChange?: (next: LlmProfilesView) => void;
}

export function AiProvidersAdminPage({
  view,
  onViewChange,
}: AiProvidersAdminPageProps) {
  const providers = useAiProviders();
  const profiles = useLlmProfiles();
  const entries = profiles.entries;

  return (
    <section className={ADMIN_PAGE_SECTION}>
      <ConsoleHeader
        title="AI providers & models"
        description="Configure how this organization reaches Claude, then select a model to add a profile for it. Each profile has its own effort level, and one profile per model can be set as the default."
        answer={
          entries && providers.entries ? (
            <ConsoleAnswer
              figure={String(entries.length)}
              words={ANSWER.profilesWords(entries.length)}
              detail={`${ANSWER.connections(providers.entries.length)} · ${ANSWER.defaults(
                entries
                  .filter((entry) => entry.is_default)
                  .map((entry) => entry.name),
              )}`}
            />
          ) : undefined
        }
      />
      <div className="flex flex-col gap-14">
        <AiProvidersSection />
        <LlmProfilesSection view={view} onViewChange={onViewChange} />
        <OtherProvidersSection />
      </div>
    </section>
  );
}
